import type { Deal } from '@coffee-to-go/common';
import markerSDK, { type MarkerReporter, type MarkerSdk } from '@marker.io/browser';
import { create } from 'zustand';
import { config } from '@/config/env';
import { showError } from './errorStore';
import type { FilterState } from './filterStore';
import type { LocationState } from './locationStore';
import type { SettingsState } from './settingsStore';

interface MarkerIoStore {
  // State
  widget: MarkerSdk | null;
  loaded: boolean;
  customData: Record<string, string>;

  // Actions
  actions: {
    loadMarkerSDK: () => Promise<void>;
    unloadMarkerSDK: () => void;
    updateReporterInfo: (reporter: MarkerReporter) => void;
    setCustomData: (data: Record<string, string>) => void;
    updateCustomData: (data: Record<string, string>) => void;
    captureFullscreen: () => void;
    updateMarkerIoDealData: (deal: Deal | null) => void;
    updateMarkerIoFilters: (filters: FilterState, searchTerm: string, sortBy: string) => void;
    updateMarkerIoSettings: (settings: SettingsState) => void;
    updateMarkerIoLocation: (userLocation: LocationState) => void;
  };
}

function getDealData(deal: Deal) {
  return {
    id: deal.kind === 'deal' ? deal.uuid || deal.id || 'unknown' : 'none',
    account_id: deal.account_id || 'unknown',
    longitude: deal.longitude.toString(),
    latitude: deal.latitude.toString(),
  };
}

export const useMarkerIoStore = create<MarkerIoStore>()((set, get) => ({
  // Initial state
  widget: null,
  loaded: false,
  customData: {},

  actions: {
    // Load MarkerIO SDK
    loadMarkerSDK: async () => {
      const projectId = config.markerIoProjectId;
      if (!projectId) return;

      try {
        const widget = await markerSDK.loadWidget({
          project: projectId,
        });
        set({ widget, loaded: true });
        widget.setCustomData(get().customData);
      } catch (error) {
        console.error('Failed to load MarkerIO SDK:', error);
      }
    },

    // Unload MarkerIO SDK
    unloadMarkerSDK: () => {
      const { widget } = get();
      widget?.unload();
      set({ widget: null, loaded: false });
    },

    // Update reporter information
    updateReporterInfo: (reporter: MarkerReporter) => {
      const { widget } = get();

      widget?.setReporter(reporter);
    },

    // Set custom data (replaces existing data)
    setCustomData: (data: Record<string, string>) => {
      const { widget } = get();
      set({ customData: data });

      widget?.setCustomData(data);
    },

    // Update custom data (merges with existing data)
    updateCustomData: (data: Record<string, string>) => {
      const { customData, widget } = get();
      const newCustomData = { ...customData, ...data };
      set({ customData: newCustomData });

      widget?.setCustomData(newCustomData);
    },

    // Capture fullscreen feedback
    captureFullscreen: () => {
      const { widget, loaded } = get();
      if (loaded && widget) {
        widget.capture('fullscreen');
      } else {
        showError('marker.io feedback tool is not loaded. Please disable any adblocking features and try again.');
      }
    },

    updateMarkerIoDealData: (deal: Deal | null) => {
      const { updateCustomData } = get().actions;
      if (deal) {
        updateCustomData({
          deal: JSON.stringify(getDealData(deal)),
        });
      }
    },

    updateMarkerIoFilters: (filters: FilterState, searchTerm: string, sortBy: string) => {
      const { updateCustomData } = get().actions;
      updateCustomData({
        filters: JSON.stringify({
          ...filters,
          searchTerm,
          sortBy,
        }),
      });
    },

    updateMarkerIoSettings: (settings: SettingsState) => {
      const { updateCustomData } = get().actions;
      updateCustomData({
        settings: JSON.stringify(settings),
      });
    },

    updateMarkerIoLocation: (userLocation: LocationState) => {
      const { updateCustomData } = get().actions;
      updateCustomData({
        location: JSON.stringify({
          location: {
            latitude: userLocation.coordinates.lat.toString(),
            longitude: userLocation.coordinates.lng.toString(),
            real: userLocation.isActualUserLocation.toString(),
          },
        }),
      });
    },
  },
}));

// Helper functions for updating specific custom data

// Selectors for common use cases
export const useMarkerIoLoaded = () => useMarkerIoStore((state) => state.loaded);
export const useMarkerIoWidget = () => useMarkerIoStore((state) => state.widget);

export const useMarkerIoActions = () => useMarkerIoStore((state) => state.actions);
